import React from "react";

/**
 * Last line of defense against a white screen. If any child throws during
 * render (a malformed token, a null field a chart didn't guard, a bad
 * image URL that breaks a component), React unmounts the whole tree — so
 * without a boundary one bad token blanks the entire app. This catches it
 * and shows a recoverable fallback while the rest of the shell (top bar,
 * footer) stays alive.
 *
 * Wrapped keyed on the route path in App.js, so navigating to another page
 * auto-clears the error without a full reload.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface it for debugging; this is intentionally the only place a
    // render crash gets logged now that it no longer takes down the app.
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          style={{
            maxWidth: 520,
            margin: "80px auto",
            padding: "32px 28px",
            textAlign: "center",
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            color: "#e6e6e6",
          }}
        >
          <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: 28, margin: "0 0 12px" }}>
            Something broke on this page
          </h2>
          <p style={{ opacity: 0.7, lineHeight: 1.5, margin: "0 0 20px" }}>
            This page hit an error. Try reloading, or head back to the home
            feed. The rest of the site is fine.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: "#00aaff",
                color: "#001018",
                border: "none",
                borderRadius: 8,
                padding: "10px 18px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = "/"; }}
              style={{
                background: "transparent",
                color: "#e6e6e6",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 8,
                padding: "10px 18px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Go home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
